import { ChangeLogEntry } from "@iventis/domain-model/model/changeLogEntry";
import { ChangeLogEntryLayerCreated } from "@iventis/domain-model/model/changeLogEntryLayerCreated";
import { ChangeLogEntryLayerDeleted } from "@iventis/domain-model/model/changeLogEntryLayerDeleted";
import { ChangeLogEntryMapObject } from "@iventis/domain-model/model/changeLogEntryMapObject";
import { VirtualScrollingChangeLogItem } from "@iventis/change-log";
import { changeLogItemAction } from "@iventis/change-log/src/change-log-item-types";
import { Content } from "@iventis/translations/content/typed-content";
import { translate } from "@iventis/translations/translation";

export const mapEntries = (entries: ChangeLogEntry[]): VirtualScrollingChangeLogItem[] => {
    const resultsWithDates = entries.map((r) => ({
        ...r,
        changedAt: new Date(r.changedAt),
    }));

    const groupedItemsByOperationId: Record<string, ChangeLogEntry[]> = {};
    for (const item of resultsWithDates) {
        if (!item.operationId) {
            continue;
        }

        if (!groupedItemsByOperationId[item.operationId]) {
            groupedItemsByOperationId[item.operationId] = [];
        }

        groupedItemsByOperationId[item.operationId].push(item);
    }

    const mappedItems: VirtualScrollingChangeLogItem[] = [];

    for (const operationGroup of Object.values(groupedItemsByOperationId)) {
        const layerItems = operationGroup.filter((r) => r.type === "LayerCreated") as (ChangeLogEntryLayerCreated | ChangeLogEntryLayerDeleted)[];
        if (!layerItems.length) {
            continue;
        }

        const operationValues = layerItems.map((item) => String(item.operation || "").toLowerCase());
        const hasCopied = operationValues.some((value) => value === "copied");
        const allImported = operationValues.every((value) => value === "imported");
        const allUnlinked = operationValues.every((value) => value === "unlinked");

        let action: changeLogItemAction = "create";
        if (allImported) {
            action = "imported";
        }
        if (allUnlinked) {
            action = "unlinked";
        }
        if (hasCopied) {
            action = "copied";
        }

        const representative = layerItems.reduce((latest, current) => ((current.changedAt?.getTime() ?? 0) > (latest.changedAt?.getTime() ?? 0) ? current : latest));
        const details = layerItems.map((item) => {
            const layerName = item.layerName || translate(Content.changeLog.labels.layerFallbackName);
            if (action === "imported") {
                return translate(Content.changeLog.details.layerImported, { layerName });
            }
            if (action === "unlinked") {
                return translate(Content.changeLog.details.layerUnlinked, { layerName });
            }
            return translate(Content.changeLog.details.layerCreated, { layerName });
        });

        const singleItem = layerItems.length === 1;
        let singularSubTitle = translate(Content.changeLog.subTitles.layerCopied);
        let pluralSubTitle = translate(Content.changeLog.subTitles.layersCopied);

        if (action === "imported") {
            singularSubTitle = translate(Content.changeLog.subTitles.layerImported);
            pluralSubTitle = translate(Content.changeLog.subTitles.layersImported);
        } else if (action === "unlinked") {
            singularSubTitle = translate(Content.changeLog.subTitles.layerUnlinked);
            pluralSubTitle = translate(Content.changeLog.subTitles.layersUnlinked);
        }

        mappedItems.push({
            id: representative.operationId || representative.id,
            title: singleItem ? representative.layerName || translate(Content.changeLog.labels.layerFallbackName) : translate(Content.changeLog.labels.multipleLayers),
            subTitle: singleItem ? singularSubTitle : pluralSubTitle,
            userName: representative.userName || "",
            time: representative.changedAt,
            action,
            details,
        });
    }

    const nonOperationalGroupedItems = resultsWithDates.filter((r) => !r.operationId);
    for (const r of nonOperationalGroupedItems) {
        if (r.type === "LayerCreated") {
            const layerCreatedItem = r as ChangeLogEntryLayerCreated;
            mappedItems.push({
                id: r.id,
                title: layerCreatedItem.layerName || "",
                subTitle: translate(Content.changeLog.subTitles.layerCreated),
                userName: r.userName || "",
                time: r.changedAt,
                action: "create",
            });
        }
        if (r.type === "LayerDeleted") {
            const layerDeletedItem = r as ChangeLogEntryLayerDeleted;

            mappedItems.push({
                id: r.id,
                title: layerDeletedItem.layerName || "",
                subTitle: translate(Content.changeLog.subTitles.layerDeleted),
                userName: r.userName || "",
                time: r.changedAt,
                action: "delete",
            });
        }
    }

    const mapObjectChanges = nonOperationalGroupedItems.filter((r) => r.type === "MapObject") as ChangeLogEntryMapObject[];

    const groupedMapObjectChanges: { [key: string]: ChangeLogEntryMapObject[] } = {};
    for (const change of mapObjectChanges) {
        const date = change.changedAt;
        const hourKey = date ? `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()} ${date.getUTCHours()}` : "unknown-hour";
        const userKey = change.userName || "Unknown User";
        const layerKey = change.layerId || "unknown-layer";
        const groupKey = `${layerKey}-${hourKey}-${userKey}`;
        if (!groupedMapObjectChanges[groupKey]) {
            groupedMapObjectChanges[groupKey] = [];
        }
        groupedMapObjectChanges[groupKey].push(change);
    }

    // eslint-disable-next-line guard-for-in
    for (const groupKey in groupedMapObjectChanges) {
        const group = groupedMapObjectChanges[groupKey];
        if (group.length > 0) {
            const representative = group.reduce((latest, current) => ((current.changedAt?.getTime() ?? 0) > (latest.changedAt?.getTime() ?? 0) ? current : latest));
            const deletedCount = group
                .filter((g) => g.operation === "Deleted")
                .map((g) => g.count)
                .reduce((a, b) => a + b, 0);
            const createdCount = group
                .filter((g) => g.operation === "Created")
                .map((g) => g.count)
                .reduce((a, b) => a + b, 0);
            const updatedCount = group
                .filter((g) => g.operation === "Updated")
                .map((g) => g.count)
                .reduce((a, b) => a + b, 0);
            const details = [];
            if (createdCount > 0) {
                details.push(
                    createdCount === 1
                        ? translate(Content.changeLog.details.mapObjectCreated, { count: createdCount })
                        : translate(Content.changeLog.details.mapObjectsCreated, { count: createdCount })
                );
            }
            if (updatedCount > 0) {
                details.push(
                    updatedCount === 1
                        ? translate(Content.changeLog.details.mapObjectUpdated, { count: updatedCount })
                        : translate(Content.changeLog.details.mapObjectsUpdated, { count: updatedCount })
                );
            }
            if (deletedCount > 0) {
                details.push(
                    deletedCount === 1
                        ? translate(Content.changeLog.details.mapObjectDeleted, { count: deletedCount })
                        : translate(Content.changeLog.details.mapObjectsDeleted, { count: deletedCount })
                );
            }

            mappedItems.push({
                id: `${groupKey}-${representative.id}`,
                title: `${representative.layerName}`,
                subTitle: translate(Content.changeLog.subTitles.layerEdited),
                userName: representative.userName || "",
                time: representative.changedAt,
                action: "update",
                details,
            });
        }
    }

    return mappedItems.sort((a, b) => b.time.getTime() - a.time.getTime());
};
